import { ListState } from '../list';

/** URL/query and UI: unset | yes | no */
export type TriState = '' | 'yes' | 'no';
/** Comparison operator for numeric filters */
export type CompareOp = '' | 'gt' | 'eq' | 'lt';

export const COMPARE_OPTIONS = [
    { key: 'none', value: '', text: '—' },
    { key: 'gt', value: 'gt', text: 'Ихээс их' },
    { key: 'eq', value: 'eq', text: 'Тэнцүү' },
    { key: 'lt', value: 'lt', text: 'Багаас бага' },
];

export const TRI_STATE_OPTIONS = [
    { key: 'all', value: '', text: 'Бүгд' },
    { key: 'yes', value: 'yes', text: 'Тийм' },
    { key: 'no', value: 'no', text: 'Үгүй' },
];

export type FilterFieldKind = 'text' | 'numeric' | 'tri' | 'bool';

export type FilterFieldKey =
    | 'unit'
    | 'size'
    | 'price'
    | 'hasClubPrice'
    | 'clubPrice'
    | 'hasDeliveryCost'
    | 'deliveryCost'
    | 'saleActive'
    | 'salePrice'
    | 'import'
    | 'deliveryDayMargin'
    | 'active';

export const FILTER_FIELD_META: { key: FilterFieldKey; text: string; kind: FilterFieldKind }[] = [
    { key: 'unit', text: 'Нэгж', kind: 'text' },
    { key: 'size', text: 'Хэмжээ', kind: 'numeric' },
    { key: 'price', text: 'Үнэ', kind: 'numeric' },
    { key: 'hasClubPrice', text: 'Клубын үнэтэй эсэх', kind: 'tri' },
    { key: 'clubPrice', text: 'Клубын үнэ (утга)', kind: 'numeric' },
    { key: 'hasDeliveryCost', text: 'Хүргэлтийн зардалтай эсэх', kind: 'tri' },
    { key: 'deliveryCost', text: 'Хүргэлтийн зардал (утга)', kind: 'numeric' },
    { key: 'saleActive', text: 'Хямдрал идэвхтэй эсэх', kind: 'tri' },
    { key: 'salePrice', text: 'Хямдралтай үнэ', kind: 'numeric' },
    { key: 'import', text: 'Өртөг тооцдог эсэх', kind: 'tri' },
    { key: 'deliveryDayMargin', text: 'Хүргэх өдөр (margin)', kind: 'numeric' },
    { key: 'active', text: 'Идэвхитэй эсэх', kind: 'tri' },
];

export const FILTER_FIELD_DROPDOWN_OPTIONS = [
    { key: 'pick', value: '', text: 'Талбар сонгох' },
    ...FILTER_FIELD_META.map((m) => ({ key: m.key, value: m.key, text: m.text })),
];

/** One filter line in the overlay (all value slots kept for simple updates). */
export interface ProductFilterRow {
    id: string;
    field: FilterFieldKey | '';
    text: string;
    op: CompareOp;
    value: string;
    tri: TriState;
    checked: boolean;
}

export function newFilterRowId(): string {
    return `f-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function emptyFilterRow(): ProductFilterRow {
    return {
        id: newFilterRowId(),
        field: '',
        text: '',
        op: '',
        value: '',
        tri: '',
        checked: false,
    };
}

export function cloneFilterRows(rows: ProductFilterRow[]): ProductFilterRow[] {
    return rows.map((r) => ({ ...r }));
}

export function fieldKind(field: FilterFieldKey): FilterFieldKind | null {
    const m = FILTER_FIELD_META.find((x) => x.key === field);
    return m ? m.kind : null;
}

/** Drop incomplete rows; bool rows only if checked. */
export function sanitizeFilterRows(rows: ProductFilterRow[]): ProductFilterRow[] {
    return rows.filter((r) => {
        if (!r.field) return false;
        const kind = fieldKind(r.field);
        if (!kind) return false;
        if (kind === 'text') return r.text.trim() !== '';
        if (kind === 'numeric') {
            return !!(r.op && r.value.trim() !== '' && !Number.isNaN(Number(r.value)));
        }
        if (kind === 'tri') return r.tri !== '';
        if (kind === 'bool') return r.checked === true;
        return false;
    });
}

export function extraFiltersFromRows(rows: ProductFilterRow[]): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const r of sanitizeFilterRows(rows)) {
        switch (r.field) {
            case 'unit':
                out.unit = r.text.trim();
                break;
            case 'size': {
                const f = buildNumericFilter(r.op, r.value);
                if (f) out.size = f;
                break;
            }
            case 'price': {
                const f = buildNumericFilter(r.op, r.value);
                if (f) out.price = f;
                break;
            }
            case 'hasClubPrice': {
                const b = triToBool(r.tri);
                if (b !== undefined) out.hasClubPrice = b;
                break;
            }
            case 'clubPrice': {
                const f = buildNumericFilter(r.op, r.value);
                if (f) out.clubPrice = f;
                break;
            }
            case 'hasDeliveryCost': {
                const b = triToBool(r.tri);
                if (b !== undefined) out.hasDeliveryCost = b;
                break;
            }
            case 'deliveryCost': {
                const f = buildNumericFilter(r.op, r.value);
                if (f) out.deliveryCost = f;
                break;
            }
            case 'saleActive': {
                const b = triToBool(r.tri);
                if (b !== undefined) out.saleActive = b;
                break;
            }
            case 'salePrice': {
                const f = buildNumericFilter(r.op, r.value);
                if (f) out.salePrice = f;
                break;
            }
            case 'import': {
                const z = triToZeroOne(r.tri);
                if (z !== undefined) out.import = z;
                break;
            }
            case 'deliveryDayMargin': {
                const f = buildNumericFilter(r.op, r.value);
                if (f) out.deliveryDayMargin = f;
                break;
            }
            case 'active': {
                const b = triToBool(r.tri);
                if (b !== undefined) out.active = b;
                break;
            }
            default:
                break;
        }
    }
    return out;
}

/** Flat shape for URL sync (legacy query param encoding). */
export type ProductListFilterFields = {
    withoutDeliveryCost: boolean;
    filterUnit: string;
    sizeOp: CompareOp;
    sizeVal: string;
    priceOp: CompareOp;
    priceVal: string;
    hasClubPriceTri: TriState;
    clubPriceOp: CompareOp;
    clubPriceVal: string;
    hasDeliveryCostTri: TriState;
    deliveryCostOp: CompareOp;
    deliveryCostVal: string;
    saleActiveTri: TriState;
    salePriceOp: CompareOp;
    salePriceVal: string;
    importTri: TriState;
    marginOp: CompareOp;
    marginVal: string;
    activeTri: TriState;
};

export const DEFAULT_PRODUCT_FILTER_FIELDS: ProductListFilterFields = {
    withoutDeliveryCost: false,
    filterUnit: '',
    sizeOp: '',
    sizeVal: '',
    priceOp: '',
    priceVal: '',
    hasClubPriceTri: '',
    clubPriceOp: '',
    clubPriceVal: '',
    hasDeliveryCostTri: '',
    deliveryCostOp: '',
    deliveryCostVal: '',
    saleActiveTri: '',
    salePriceOp: '',
    salePriceVal: '',
    importTri: '',
    marginOp: '',
    marginVal: '',
    activeTri: '',
};

export function rowsToFilterFields(rows: ProductFilterRow[]): ProductListFilterFields {
    const f = { ...DEFAULT_PRODUCT_FILTER_FIELDS };
    for (const r of sanitizeFilterRows(rows)) {
        switch (r.field) {
            case 'unit':
                f.filterUnit = r.text.trim();
                break;
            case 'size':
                f.sizeOp = r.op;
                f.sizeVal = r.value;
                break;
            case 'price':
                f.priceOp = r.op;
                f.priceVal = r.value;
                break;
            case 'hasClubPrice':
                f.hasClubPriceTri = r.tri;
                break;
            case 'clubPrice':
                f.clubPriceOp = r.op;
                f.clubPriceVal = r.value;
                break;
            case 'hasDeliveryCost':
                f.hasDeliveryCostTri = r.tri;
                break;
            case 'deliveryCost':
                f.deliveryCostOp = r.op;
                f.deliveryCostVal = r.value;
                break;
            case 'saleActive':
                f.saleActiveTri = r.tri;
                break;
            case 'salePrice':
                f.salePriceOp = r.op;
                f.salePriceVal = r.value;
                break;
            case 'import':
                f.importTri = r.tri;
                break;
            case 'deliveryDayMargin':
                f.marginOp = r.op;
                f.marginVal = r.value;
                break;
            case 'active':
                f.activeTri = r.tri;
                break;
            default:
                break;
        }
    }
    return f;
}

export function filterFieldsToRows(f: ProductListFilterFields): ProductFilterRow[] {
    const rows: ProductFilterRow[] = [];
    if (f.filterUnit.trim()) {
        rows.push({
            id: newFilterRowId(),
            field: 'unit',
            text: f.filterUnit,
            op: '',
            value: '',
            tri: '',
            checked: false,
        });
    }
    if (f.sizeOp && f.sizeVal !== '') {
        rows.push({
            id: newFilterRowId(),
            field: 'size',
            text: '',
            op: f.sizeOp,
            value: f.sizeVal,
            tri: '',
            checked: false,
        });
    }
    if (f.priceOp && f.priceVal !== '') {
        rows.push({
            id: newFilterRowId(),
            field: 'price',
            text: '',
            op: f.priceOp,
            value: f.priceVal,
            tri: '',
            checked: false,
        });
    }
    if (f.hasClubPriceTri) {
        rows.push({
            id: newFilterRowId(),
            field: 'hasClubPrice',
            text: '',
            op: '',
            value: '',
            tri: f.hasClubPriceTri,
            checked: false,
        });
    }
    if (f.clubPriceOp && f.clubPriceVal !== '') {
        rows.push({
            id: newFilterRowId(),
            field: 'clubPrice',
            text: '',
            op: f.clubPriceOp,
            value: f.clubPriceVal,
            tri: '',
            checked: false,
        });
    }
    if (f.hasDeliveryCostTri) {
        rows.push({
            id: newFilterRowId(),
            field: 'hasDeliveryCost',
            text: '',
            op: '',
            value: '',
            tri: f.hasDeliveryCostTri,
            checked: false,
        });
    }
    if (f.deliveryCostOp && f.deliveryCostVal !== '') {
        rows.push({
            id: newFilterRowId(),
            field: 'deliveryCost',
            text: '',
            op: f.deliveryCostOp,
            value: f.deliveryCostVal,
            tri: '',
            checked: false,
        });
    }
    if (f.saleActiveTri) {
        rows.push({
            id: newFilterRowId(),
            field: 'saleActive',
            text: '',
            op: '',
            value: '',
            tri: f.saleActiveTri,
            checked: false,
        });
    }
    if (f.salePriceOp && f.salePriceVal !== '') {
        rows.push({
            id: newFilterRowId(),
            field: 'salePrice',
            text: '',
            op: f.salePriceOp,
            value: f.salePriceVal,
            tri: '',
            checked: false,
        });
    }
    if (f.importTri) {
        rows.push({
            id: newFilterRowId(),
            field: 'import',
            text: '',
            op: '',
            value: '',
            tri: f.importTri,
            checked: false,
        });
    }
    if (f.marginOp && f.marginVal !== '') {
        rows.push({
            id: newFilterRowId(),
            field: 'deliveryDayMargin',
            text: '',
            op: f.marginOp,
            value: f.marginVal,
            tri: '',
            checked: false,
        });
    }
    if (f.activeTri) {
        rows.push({
            id: newFilterRowId(),
            field: 'active',
            text: '',
            op: '',
            value: '',
            tri: f.activeTri,
            checked: false,
        });
    }
    return rows;
}

export function triFromParam(raw: string | null): TriState {
    if (raw === '1' || raw === 'true' || raw === 'yes') return 'yes';
    if (raw === '0' || raw === 'false' || raw === 'no') return 'no';
    return '';
}

export function compareOpFromParam(raw: string | null): CompareOp {
    if (raw === 'gt' || raw === 'eq' || raw === 'lt') return raw;
    return '';
}

export function triToQueryValue(t: TriState): string | null {
    if (t === 'yes') return '1';
    if (t === 'no') return '0';
    return null;
}

export function buildNumericFilter(op: CompareOp, val: string): { op: 'gt' | 'eq' | 'lt'; value: string } | undefined {
    if (!op || val === undefined || val === null || String(val).trim() === '') return undefined;
    const n = Number(val);
    if (Number.isNaN(n)) return undefined;
    return { op: op as 'gt' | 'eq' | 'lt', value: String(val).trim() };
}

export function triToBool(t: TriState): boolean | undefined {
    if (t === 'yes') return true;
    if (t === 'no') return false;
    return undefined;
}

/** API stores `import` as 0 | 1 (not boolean). */
export function triToZeroOne(t: TriState): 0 | 1 | undefined {
    if (t === 'yes') return 1;
    if (t === 'no') return 0;
    return undefined;
}

export function parseProductFilterParams(search: string): ProductListFilterFields {
    const params = new URLSearchParams(search);
    return {
        withoutDeliveryCost: params.get('withoutDelivery') === '1' || params.get('withoutDelivery') === 'true',
        filterUnit: params.get('unit') ?? '',
        sizeOp: compareOpFromParam(params.get('sizeOp')),
        sizeVal: params.get('sizeVal') ?? '',
        priceOp: compareOpFromParam(params.get('priceOp')),
        priceVal: params.get('priceVal') ?? '',
        hasClubPriceTri: triFromParam(params.get('hasClub')),
        clubPriceOp: compareOpFromParam(params.get('clubPriceOp')),
        clubPriceVal: params.get('clubPriceVal') ?? '',
        hasDeliveryCostTri: triFromParam(params.get('hasDeliv')),
        deliveryCostOp: compareOpFromParam(params.get('delivCostOp')),
        deliveryCostVal: params.get('delivCostVal') ?? '',
        saleActiveTri: triFromParam(params.get('saleAct')),
        salePriceOp: compareOpFromParam(params.get('salePriceOp')),
        salePriceVal: params.get('salePriceVal') ?? '',
        importTri: triFromParam(params.get('prodImport')),
        marginOp: compareOpFromParam(params.get('marginOp')),
        marginVal: params.get('marginVal') ?? '',
        activeTri: triFromParam(params.get('prodAct')),
    };
}

export function syncFilterQueryParams(params: URLSearchParams, f: ProductListFilterFields) {
    if (f.withoutDeliveryCost) params.set('withoutDelivery', '1');
    if (f.filterUnit.trim()) params.set('unit', f.filterUnit.trim());
    if (f.sizeOp) {
        params.set('sizeOp', f.sizeOp);
        if (f.sizeVal !== '') params.set('sizeVal', f.sizeVal);
    }
    if (f.priceOp) {
        params.set('priceOp', f.priceOp);
        if (f.priceVal !== '') params.set('priceVal', f.priceVal);
    }
    const hc = triToQueryValue(f.hasClubPriceTri);
    if (hc != null) params.set('hasClub', hc);
    if (f.clubPriceOp) {
        params.set('clubPriceOp', f.clubPriceOp);
        if (f.clubPriceVal !== '') params.set('clubPriceVal', f.clubPriceVal);
    }
    const hd = triToQueryValue(f.hasDeliveryCostTri);
    if (hd != null) params.set('hasDeliv', hd);
    if (f.deliveryCostOp) {
        params.set('delivCostOp', f.deliveryCostOp);
        if (f.deliveryCostVal !== '') params.set('delivCostVal', f.deliveryCostVal);
    }
    const sa = triToQueryValue(f.saleActiveTri);
    if (sa != null) params.set('saleAct', sa);
    if (f.salePriceOp) {
        params.set('salePriceOp', f.salePriceOp);
        if (f.salePriceVal !== '') params.set('salePriceVal', f.salePriceVal);
    }
    const pi = triToQueryValue(f.importTri);
    if (pi != null) params.set('prodImport', pi);
    if (f.marginOp) {
        params.set('marginOp', f.marginOp);
        if (f.marginVal !== '') params.set('marginVal', f.marginVal);
    }
    const pa = triToQueryValue(f.activeTri);
    if (pa != null) params.set('prodAct', pa);
}

export function productListRequestBody(
    s: ListState,
    pageOverride: { page: number; size: number }
): Record<string, unknown> {
    const fromRows = extraFiltersFromRows(s.productFilterRows ?? []);
    return {
        providerId: s.value1,
        name: s.value2,
        barCode: s.value3,
        ...fromRows,
        page: pageOverride,
    };
}

export function activeFilterCount(rows: ProductFilterRow[] | undefined): number {
    return sanitizeFilterRows(rows ?? []).length;
}
